<template>
  <section class="h-full w-full flex flex-col bg-gray-50 overflow-auto">
    <!-- Header -->
    <div class="w-full bg-white border-b shadow-sm sticky top-0 z-10">
      <div class="px-6 py-4">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">📊 Reporte Avanzado de Cotizaciones</h1>
            <p class="text-sm text-gray-600 mt-1">Análisis completo de ventas, conversiones y proyecciones</p>
          </div>
          
          <!-- Controles de período -->
          <div class="flex items-center gap-3">
            <div class="flex flex-col">
              <label class="text-xs font-medium text-gray-700 mb-1">Año</label>
              <select v-model="filtros.ano" @change="cargarReporte" 
                      class="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            
            <div class="flex flex-col">
              <label class="text-xs font-medium text-gray-700 mb-1">Mes</label>
              <select v-model="filtros.mes" @change="cargarReporte" 
                      class="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                <option v-for="(mes, index) in meses" :key="index" :value="index + 1">{{ mes }}</option>
              </select>
            </div>

            <div class="flex flex-col justify-end">
              <label class="flex items-center text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" v-model="filtros.comparar_mes_anterior" @change="cargarReporte" 
                       class="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                Comparar con mes anterior
              </label>
            </div>

            <button @click="cargarReporte" :disabled="cargando"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <i class="las la-sync-alt mr-2" :class="{'la-spin': cargando}"></i>
              {{ cargando ? 'Cargando...' : 'Actualizar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Alertas Críticas -->
    <div v-if="mostrarAlertasCriticas" class="px-6 pt-4">
      <div class="bg-red-50 border-l-4 border-red-600 p-4 rounded-lg">
        <div class="flex items-start">
          <i class="las la-exclamation-triangle text-red-600 text-2xl mr-3"></i>
          <div class="flex-1">
            <h3 class="text-red-900 font-bold mb-2">⚠️ {{ reporte.alertas.criticas }} Alerta(s) Crítica(s)</h3>
            <div class="space-y-2">
              <div v-for="alerta in alertasCriticas" :key="'critica-' + alerta.categoria"
                   class="bg-white p-3 rounded border-l-2 border-red-500">
                <p class="font-semibold text-red-900">{{ alerta.mensaje }}</p>
                <p class="text-sm text-gray-700 mt-1">💡 <span class="font-medium">Recomendación:</span> {{ alerta.recomendacion }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contenido Principal -->
    <div v-if="cargando" class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <i class="las la-spinner la-spin text-6xl text-blue-600"></i>
        <p class="mt-4 text-gray-600">Generando reporte avanzado...</p>
      </div>
    </div>

    <div v-else-if="error" class="flex-1 flex items-center justify-center px-6">
      <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg text-center">
        <i class="las la-exclamation-circle text-5xl text-red-600"></i>
        <h3 class="text-lg font-bold text-red-900 mt-4">Error al cargar el reporte</h3>
        <p class="text-red-700 mt-2">{{ error }}</p>
        <button @click="cargarReporte" class="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
          Reintentar
        </button>
      </div>
    </div>

    <div v-else-if="reporte" class="flex-1 px-6 py-4 space-y-6">
      
      <!-- Metadata -->
      <div class="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg p-6 shadow-lg">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-2xl font-bold">{{ reporte.metadata.nombre_mes }} {{ reporte.metadata.ano }}</h2>
            <p class="text-blue-100 mt-1">
              <span v-if="reporte.metadata.es_mes_vigente" class="inline-flex items-center">
                <i class="las la-clock mr-2"></i> Mes vigente - Día {{ reporte.metadata.dia_actual }} de {{ reporte.metadata.dias_en_mes }}
              </span>
              <span v-else>Mes finalizado</span>
            </p>
          </div>
          <div class="text-right text-sm text-blue-100">
            <p>Generado: {{ formatFecha(reporte.metadata.fecha_generacion) }}</p>
          </div>
        </div>
      </div>

      <!-- KPIs Principales -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Cotizaciones</p>
              <p class="text-3xl font-bold text-gray-900 mt-2">{{ reporte.metricas_generales.total_cotizaciones }}</p>
              <p v-if="reporte.comparativas && reporte.comparativas.aplicable" class="text-xs mt-2"
                 :class="reporte.comparativas.variacion_cotizaciones.cantidad >= 0 ? 'text-green-600' : 'text-red-600'">
                <i class="las" :class="reporte.comparativas.variacion_cotizaciones.cantidad >= 0 ? 'la-arrow-up' : 'la-arrow-down'"></i>
                {{ Math.abs(reporte.comparativas.variacion_cotizaciones.porcentaje).toFixed(2) }}% vs mes anterior
              </p>
            </div>
            <i class="las la-file-invoice text-4xl text-blue-500 opacity-20"></i>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Ventas Cerradas</p>
              <p class="text-3xl font-bold text-gray-900 mt-2">{{ reporte.metricas_generales.total_ventas }}</p>
              <p v-if="reporte.comparativas && reporte.comparativas.aplicable" class="text-xs mt-2"
                 :class="reporte.comparativas.variacion_ventas.cantidad >= 0 ? 'text-green-600' : 'text-red-600'">
                <i class="las" :class="reporte.comparativas.variacion_ventas.cantidad >= 0 ? 'la-arrow-up' : 'la-arrow-down'"></i>
                {{ Math.abs(reporte.comparativas.variacion_ventas.porcentaje).toFixed(2) }}% vs mes anterior
              </p>
            </div>
            <i class="las la-check-circle text-4xl text-green-500 opacity-20"></i>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Tasa de Conversión</p>
              <p class="text-3xl font-bold text-gray-900 mt-2">{{ reporte.metricas_generales.tasa_conversion_general.toFixed(2) }}%</p>
              <p v-if="reporte.comparativas && reporte.comparativas.aplicable" class="text-xs mt-2"
                 :class="reporte.comparativas.variacion_tasa_conversion.puntos >= 0 ? 'text-green-600' : 'text-red-600'">
                <i class="las" :class="reporte.comparativas.variacion_tasa_conversion.puntos >= 0 ? 'la-arrow-up' : 'la-arrow-down'"></i>
                {{ Math.abs(reporte.comparativas.variacion_tasa_conversion.puntos).toFixed(2) }} puntos vs mes anterior
              </p>
            </div>
            <i class="las la-percentage text-4xl text-yellow-500 opacity-20"></i>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Ticket Promedio</p>
              <p class="text-3xl font-bold text-gray-900 mt-2">${{ formatMonto(reporte.metricas_generales.ticket_promedio_ventas) }}</p>
              <p v-if="reporte.comparativas && reporte.comparativas.aplicable" class="text-xs mt-2"
                 :class="reporte.comparativas.variacion_ticket_promedio.cantidad >= 0 ? 'text-green-600' : 'text-red-600'">
                <i class="las" :class="reporte.comparativas.variacion_ticket_promedio.cantidad >= 0 ? 'la-arrow-up' : 'la-arrow-down'"></i>
                {{ Math.abs(reporte.comparativas.variacion_ticket_promedio.porcentaje).toFixed(2) }}% vs mes anterior
              </p>
            </div>
            <i class="las la-dollar-sign text-4xl text-purple-500 opacity-20"></i>
          </div>
        </div>
      </div>

      <!-- Métricas Financieras -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <p class="text-sm font-medium opacity-90">💰 Monto Total Ventas</p>
          <p class="text-3xl font-bold mt-2">${{ formatMonto(reporte.metricas_generales.monto_total_ventas) }}</p>
          <p class="text-xs mt-3 opacity-75">Total facturado en el período</p>
        </div>

        <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <p class="text-sm font-medium opacity-90">💵 Total Cobrado</p>
          <p class="text-3xl font-bold mt-2">${{ formatMonto(reporte.analisis_financiero.resumen_cobros.total_cobrado) }}</p>
          <p class="text-xs mt-3 opacity-75">{{ reporte.analisis_financiero.margen_cobrado.toFixed(2) }}% del total facturado</p>
        </div>

        <div class="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
          <p class="text-sm font-medium opacity-90">⏳ Saldo Pendiente</p>
          <p class="text-3xl font-bold mt-2">${{ formatMonto(reporte.analisis_financiero.cuentas_por_cobrar.saldo_pendiente) }}</p>
          <p class="text-xs mt-3 opacity-75">{{ reporte.analisis_financiero.cuentas_por_cobrar.ventas_con_saldo }} ventas con saldo</p>
        </div>
      </div>

      <!-- Proyecciones (solo mes vigente) -->
      <div v-if="reporte.proyecciones && reporte.proyecciones.aplicable" class="bg-white rounded-lg shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <i class="las la-chart-line text-blue-600 text-2xl mr-2"></i>
          Proyecciones para Fin de Mes
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div class="bg-blue-50 rounded-lg p-4">
              <p class="text-sm text-gray-600 mb-3">📊 Promedios Diarios</p>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-sm text-gray-700">Ventas/día:</span>
                  <span class="font-bold text-gray-900">{{ reporte.proyecciones.promedios_diarios.ventas.toFixed(2) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-700">Monto/día:</span>
                  <span class="font-bold text-gray-900">${{ formatMonto(reporte.proyecciones.promedios_diarios.monto) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-700">Cotizaciones/día:</span>
                  <span class="font-bold text-gray-900">{{ reporte.proyecciones.promedios_diarios.cotizaciones }}</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div class="bg-green-50 rounded-lg p-4">
              <p class="text-sm text-gray-600 mb-3">🎯 Proyección Fin de Mes</p>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-sm text-gray-700">Ventas estimadas:</span>
                  <span class="font-bold text-green-700">{{ Math.round(reporte.proyecciones.proyeccion_fin_mes.ventas_estimadas) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-700">Monto estimado:</span>
                  <span class="font-bold text-green-700">${{ formatMonto(reporte.proyecciones.proyeccion_fin_mes.monto_estimado) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-700">Cotizaciones estimadas:</span>
                  <span class="font-bold text-green-700">{{ Math.round(reporte.proyecciones.proyeccion_fin_mes.cotizaciones_estimadas) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
          <p class="text-sm font-medium text-yellow-900">⚡ Para alcanzar la meta del mes:</p>
          <div class="mt-2 grid grid-cols-2 gap-4">
            <div>
              <span class="text-xs text-yellow-700">Ventas necesarias diarias:</span>
              <p class="text-lg font-bold text-yellow-900">{{ reporte.proyecciones.para_alcanzar_meta.ventas_necesarias_diarias.toFixed(2) }}</p>
            </div>
            <div>
              <span class="text-xs text-yellow-700">Monto necesario diario:</span>
              <p class="text-lg font-bold text-yellow-900">${{ formatMonto(reporte.proyecciones.para_alcanzar_meta.monto_necesario_diario) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Embudo de Ventas -->
      <div class="bg-white rounded-lg shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <i class="las la-filter text-purple-600 text-2xl mr-2"></i>
          Embudo de Ventas
        </h3>
        <div class="space-y-3">
          <div v-for="etapa in etapasEmbudo" :key="'embudo-' + etapa.etapa" class="relative">
            <div class="flex items-center gap-3">
              <div class="w-16 text-right">
                <span class="text-sm font-bold text-gray-700">{{ etapa.porcentaje.toFixed(1) }}%</span>
              </div>
              <div class="flex-1">
                <div class="bg-gray-200 rounded-full h-8 overflow-hidden">
                  <div class="bg-gradient-to-r from-purple-500 to-purple-600 h-full flex items-center px-4 transition-all duration-500"
                       :style="{width: etapa.porcentaje + '%'}">
                    <span class="text-white text-sm font-medium whitespace-nowrap">{{ etapa.etapa }}</span>
                  </div>
                </div>
              </div>
              <div class="w-20 text-left">
                <span class="text-sm font-bold text-gray-900">{{ etapa.cantidad }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="reporte.embudo_ventas.tasa_abandono" class="mt-6 p-4 bg-red-50 rounded-lg">
          <p class="text-sm font-medium text-red-900 mb-3">📉 Tasas de Abandono</p>
          <div class="grid grid-cols-3 gap-4 text-center">
            <div v-for="(tasa, key) in reporte.embudo_ventas.tasa_abandono" :key="key">
              <p class="text-xs text-red-700">{{ key.replace('_', ' → ') }}</p>
              <p class="text-lg font-bold text-red-900">{{ tasa.toFixed(1) }}%</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Performance de Vendedores -->
      <div class="bg-white rounded-lg shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <i class="las la-users text-green-600 text-2xl mr-2"></i>
          Performance de Vendedores
        </h3>

        <!-- Resumen por clasificación -->
        <div class="grid grid-cols-5 gap-4 mb-6">
          <div class="bg-yellow-50 rounded-lg p-4 text-center border-2 border-yellow-400">
            <i class="las la-trophy text-3xl text-yellow-600"></i>
            <p class="text-2xl font-bold text-yellow-900 mt-2">{{ reporte.performance_vendedores.resumen_por_clasificacion.top_performer || 0 }}</p>
            <p class="text-xs text-yellow-700 uppercase">Top Performer</p>
          </div>
          <div class="bg-green-50 rounded-lg p-4 text-center border-2 border-green-400">
            <i class="las la-star text-3xl text-green-600"></i>
            <p class="text-2xl font-bold text-green-900 mt-2">{{ reporte.performance_vendedores.resumen_por_clasificacion.alto || 0 }}</p>
            <p class="text-xs text-green-700 uppercase">Alto</p>
          </div>
          <div class="bg-blue-50 rounded-lg p-4 text-center border-2 border-blue-400">
            <i class="las la-equals text-3xl text-blue-600"></i>
            <p class="text-2xl font-bold text-blue-900 mt-2">{{ reporte.performance_vendedores.resumen_por_clasificacion.medio || 0 }}</p>
            <p class="text-xs text-blue-700 uppercase">Medio</p>
          </div>
          <div class="bg-orange-50 rounded-lg p-4 text-center border-2 border-orange-400">
            <i class="las la-arrow-down text-3xl text-orange-600"></i>
            <p class="text-2xl font-bold text-orange-900 mt-2">{{ reporte.performance_vendedores.resumen_por_clasificacion.bajo || 0 }}</p>
            <p class="text-xs text-orange-700 uppercase">Bajo</p>
          </div>
          <div class="bg-red-50 rounded-lg p-4 text-center border-2 border-red-400">
            <i class="las la-times-circle text-3xl text-red-600"></i>
            <p class="text-2xl font-bold text-red-900 mt-2">{{ reporte.performance_vendedores.resumen_por_clasificacion.sin_ventas || 0 }}</p>
            <p class="text-xs text-red-700 uppercase">Sin Ventas</p>
          </div>
        </div>

        <!-- Top 5 por ventas -->
        <div class="mb-6">
          <h4 class="font-bold text-gray-800 mb-3">🏆 Top 5 por Monto de Ventas</h4>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cotizaciones</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ventas</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conv. %</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clasificación</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(vendedor, index) in reporte.performance_vendedores.top_5_por_ventas" :key="vendedor.id"
                    :class="index === 0 ? 'bg-yellow-50' : ''">
                  <td class="px-4 py-3 text-sm">
                    <span v-if="index === 0" class="text-2xl">🥇</span>
                    <span v-else-if="index === 1" class="text-2xl">🥈</span>
                    <span v-else-if="index === 2" class="text-2xl">🥉</span>
                    <span v-else class="font-medium text-gray-600">{{ index + 1 }}</span>
                  </td>
                  <td class="px-4 py-3 text-sm font-medium text-gray-900">{{ vendedor.name }}</td>
                  <td class="px-4 py-3 text-sm text-gray-600">{{ vendedor.total_cotizaciones }}</td>
                  <td class="px-4 py-3 text-sm text-gray-600">{{ vendedor.total_ventas }}</td>
                  <td class="px-4 py-3 text-sm font-semibold text-green-700">${{ formatMonto(vendedor.monto_ventas) }}</td>
                  <td class="px-4 py-3 text-sm">
                    <span class="px-2 py-1 rounded-full text-xs font-medium"
                          :class="getConversionClass(vendedor.tasa_conversion)">
                      {{ vendedor.tasa_conversion }}%
                    </span>
                  </td>
                  <td class="px-4 py-3 text-sm">
                    <span class="px-2 py-1 rounded-full text-xs font-medium"
                          :class="getClasificacionClass(vendedor.clasificacion)">
                      {{ vendedor.clasificacion.replace('_', ' ') }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Top 5 por conversión -->
        <div>
          <h4 class="font-bold text-gray-800 mb-3">🎯 Top 5 por Tasa de Conversión</h4>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conv. %</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ventas</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cotizaciones</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Eficiencia</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(vendedor, index) in reporte.performance_vendedores.top_5_por_conversion" :key="vendedor.id">
                  <td class="px-4 py-3 text-sm font-medium text-gray-600">{{ index + 1 }}</td>
                  <td class="px-4 py-3 text-sm font-medium text-gray-900">{{ vendedor.name }}</td>
                  <td class="px-4 py-3 text-sm">
                    <span class="px-3 py-1 rounded-full text-sm font-bold"
                          :class="getConversionClass(vendedor.tasa_conversion)">
                      {{ vendedor.tasa_conversion }}%
                    </span>
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600">{{ vendedor.total_ventas }}</td>
                  <td class="px-4 py-3 text-sm text-gray-600">{{ vendedor.total_cotizaciones }}</td>
                  <td class="px-4 py-3 text-sm font-semibold text-blue-700">{{ vendedor.eficiencia?.toFixed(2) || 'N/A' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Análisis de Productos -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Productos Estrella -->
        <div class="bg-white rounded-lg shadow-lg p-6">
          <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <i class="las la-star text-yellow-500 text-2xl mr-2"></i>
            Productos Estrella
          </h3>
          <p class="text-sm text-gray-600 mb-4">Alta conversión y buen monto de ventas</p>
          <div class="space-y-3">
            <div v-for="producto in reporte.analisis_productos.productos_estrella.slice(0, 5)" :key="producto.id"
                 class="bg-green-50 rounded-lg p-3 border border-green-200">
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <p class="font-semibold text-gray-900">{{ producto.nombre }}</p>
                  <div class="mt-2 flex gap-4 text-xs text-gray-600">
                    <span>📊 {{ producto.cotizaciones_con_producto }} cotizaciones</span>
                    <span>✅ {{ producto.cantidad_vendida }} vendidos</span>
                  </div>
                </div>
                <div class="text-right">
                  <p class="text-sm font-bold text-green-700">${{ formatMonto(producto.monto_vendido) }}</p>
                  <p class="text-xs text-green-600 mt-1">{{ producto.tasa_conversion }}% conv.</p>
                </div>
              </div>
            </div>
            <p v-if="reporte.analisis_productos.productos_estrella.length === 0" class="text-center text-gray-500 py-4">
              No hay productos estrella en este período
            </p>
          </div>
        </div>

        <!-- Productos Problemáticos -->
        <div class="bg-white rounded-lg shadow-lg p-6">
          <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <i class="las la-exclamation-triangle text-red-500 text-2xl mr-2"></i>
            Productos Problemáticos
          </h3>
          <p class="text-sm text-gray-600 mb-4">Muchas cotizaciones, baja conversión (&lt;10%)</p>
          <div class="space-y-3">
            <div v-for="producto in reporte.analisis_productos.productos_problematicos.slice(0, 5)" :key="producto.id"
                 class="bg-red-50 rounded-lg p-3 border border-red-200">
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <p class="font-semibold text-gray-900">{{ producto.nombre }}</p>
                  <div class="mt-2 flex gap-4 text-xs text-gray-600">
                    <span>📊 {{ producto.cotizaciones_con_producto }} cotizaciones</span>
                    <span>❌ {{ producto.cantidad_vendida }} vendidos</span>
                  </div>
                </div>
                <div class="text-right">
                  <p class="text-sm font-bold text-red-700">${{ formatMonto(producto.monto_vendido) }}</p>
                  <p class="text-xs text-red-600 mt-1">{{ producto.tasa_conversion }}% conv.</p>
                </div>
              </div>
            </div>
            <p v-if="reporte.analisis_productos.productos_problematicos.length === 0" class="text-center text-gray-500 py-4">
              ✅ No hay productos problemáticos
            </p>
          </div>
        </div>
      </div>

      <!-- Análisis de Clientes -->
      <div class="bg-white rounded-lg shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <i class="las la-user-friends text-blue-600 text-2xl mr-2"></i>
          Análisis de Clientes
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div class="bg-blue-50 rounded-lg p-4 text-center">
            <p class="text-sm text-blue-700">Total Clientes</p>
            <p class="text-3xl font-bold text-blue-900 mt-2">{{ reporte.analisis_clientes.resumen_general.total_clientes_unicos }}</p>
          </div>
          <div class="bg-green-50 rounded-lg p-4 text-center">
            <p class="text-sm text-green-700">Nuevos</p>
            <p class="text-3xl font-bold text-green-900 mt-2">{{ reporte.analisis_clientes.resumen_general.clientes_nuevos }}</p>
            <p class="text-xs text-green-600 mt-1">${{ formatMonto(reporte.analisis_clientes.resumen_general.monto_clientes_nuevos) }}</p>
          </div>
          <div class="bg-purple-50 rounded-lg p-4 text-center">
            <p class="text-sm text-purple-700">Recurrentes</p>
            <p class="text-3xl font-bold text-purple-900 mt-2">{{ reporte.analisis_clientes.resumen_general.clientes_recurrentes }}</p>
            <p class="text-xs text-purple-600 mt-1">${{ formatMonto(reporte.analisis_clientes.resumen_general.monto_clientes_recurrentes) }}</p>
          </div>
          <div class="bg-yellow-50 rounded-lg p-4 text-center">
            <p class="text-sm text-yellow-700">Tasa Recurrencia</p>
            <p class="text-3xl font-bold text-yellow-900 mt-2">{{ reporte.analisis_clientes.tasa_clientes_recurrentes }}%</p>
          </div>
        </div>

        <!-- Análisis por origen -->
        <div v-if="reporte.analisis_clientes.analisis_por_origen && reporte.analisis_clientes.analisis_por_origen.length > 0">
          <h4 class="font-bold text-gray-800 mb-3">📍 Clientes por Origen</h4>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div v-for="origen in reporte.analisis_clientes.analisis_por_origen.slice(0, 8)" :key="origen.origen"
                 class="bg-gray-50 rounded-lg p-3 border">
              <p class="text-xs text-gray-600 uppercase">{{ origen.origen }}</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">{{ origen.cantidad_clientes }}</p>
              <p class="text-xs text-gray-600 mt-1">{{ origen.total_ventas }} ventas</p>
              <p class="text-xs font-semibold text-green-700">${{ formatMonto(origen.monto_ventas) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Estado de Producción -->
      <div class="bg-white rounded-lg shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <i class="las la-cogs text-orange-600 text-2xl mr-2"></i>
          Estado de Producción
        </h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="bg-blue-50 rounded-lg p-4">
            <p class="text-sm text-blue-700">Total Productos</p>
            <p class="text-3xl font-bold text-blue-900 mt-2">{{ reporte.estado_produccion.estado_general.total_productos }}</p>
          </div>
          <div class="bg-green-50 rounded-lg p-4">
            <p class="text-sm text-green-700">En Producción</p>
            <p class="text-3xl font-bold text-green-900 mt-2">{{ reporte.estado_produccion.estado_general.en_produccion }}</p>
            <div class="mt-2 bg-green-200 rounded-full h-2">
              <div class="bg-green-600 h-2 rounded-full" :style="{width: reporte.estado_produccion.eficiencia.tasa_produccion_completa + '%'}"></div>
            </div>
          </div>
          <div class="bg-purple-50 rounded-lg p-4">
            <p class="text-sm text-purple-700">Con Instalación</p>
            <p class="text-3xl font-bold text-purple-900 mt-2">{{ reporte.estado_produccion.estado_general.con_instalacion }}</p>
          </div>
          <div class="bg-yellow-50 rounded-lg p-4">
            <p class="text-sm text-yellow-700">Instalados</p>
            <p class="text-3xl font-bold text-yellow-900 mt-2">{{ reporte.estado_produccion.estado_general.instalacion_completa }}</p>
            <p class="text-xs text-yellow-600 mt-1">{{ reporte.estado_produccion.eficiencia.tasa_instalacion_completa }}%</p>
          </div>
        </div>

        <div v-if="reporte.estado_produccion.productos_atrasados && reporte.estado_produccion.productos_atrasados.length > 0" 
             class="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
          <p class="font-bold text-red-900">⚠️ {{ reporte.estado_produccion.cantidad_productos_atrasados }} Productos Atrasados</p>
        </div>
      </div>

      <!-- Otras Alertas y Advertencias -->
      <div v-if="reporte.alertas && (reporte.alertas.advertencias > 0 || reporte.alertas.informativas > 0)" class="space-y-4">
        <!-- Advertencias -->
        <div v-if="reporte.alertas.advertencias > 0" class="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
          <h3 class="font-bold text-yellow-900 mb-3">⚡ {{ reporte.alertas.advertencias }} Advertencia(s)</h3>
          <div class="space-y-2">
            <div v-for="alerta in alertasAdvertencias" :key="'adv-' + alerta.categoria"
                 class="bg-white p-3 rounded border-l-2 border-yellow-400">
              <p class="font-semibold text-yellow-900">{{ alerta.mensaje }}</p>
              <p class="text-sm text-gray-700 mt-1">💡 {{ alerta.recomendacion }}</p>
            </div>
          </div>
        </div>

        <!-- Informativas -->
        <div v-if="reporte.alertas.informativas > 0" class="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
          <h3 class="font-bold text-blue-900 mb-3">ℹ️ {{ reporte.alertas.informativas }} Info(s)</h3>
          <div class="space-y-2">
            <div v-for="alerta in alertasInfo" :key="'info-' + alerta.categoria"
                 class="bg-white p-3 rounded border-l-2 border-blue-400">
              <p class="font-semibold text-blue-900">{{ alerta.mensaje }}</p>
              <p class="text-sm text-gray-700 mt-1">💡 {{ alerta.recomendacion }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Análisis de Calidad -->
      <div class="bg-white rounded-lg shadow-lg p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <i class="las la-check-double text-green-600 text-2xl mr-2"></i>
          Análisis de Calidad de Datos
        </h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="text-center p-4 rounded-lg" 
               :class="reporte.analisis_calidad.cotizaciones_sin_vendedor === 0 ? 'bg-green-50' : 'bg-red-50'">
            <p class="text-2xl font-bold" :class="reporte.analisis_calidad.cotizaciones_sin_vendedor === 0 ? 'text-green-900' : 'text-red-900'">
              {{ reporte.analisis_calidad.cotizaciones_sin_vendedor }}
            </p>
            <p class="text-xs text-gray-600 mt-1">Sin Vendedor</p>
          </div>
          <div class="text-center p-4 rounded-lg" 
               :class="reporte.analisis_calidad.cotizaciones_sin_cliente === 0 ? 'bg-green-50' : 'bg-red-50'">
            <p class="text-2xl font-bold" :class="reporte.analisis_calidad.cotizaciones_sin_cliente === 0 ? 'text-green-900' : 'text-red-900'">
              {{ reporte.analisis_calidad.cotizaciones_sin_cliente }}
            </p>
            <p class="text-xs text-gray-600 mt-1">Sin Cliente</p>
          </div>
          <div class="text-center p-4 rounded-lg" 
               :class="reporte.analisis_calidad.ventas_sin_abonos === 0 ? 'bg-green-50' : 'bg-red-50'">
            <p class="text-2xl font-bold" :class="reporte.analisis_calidad.ventas_sin_abonos === 0 ? 'text-green-900' : 'text-red-900'">
              {{ reporte.analisis_calidad.ventas_sin_abonos }}
            </p>
            <p class="text-xs text-gray-600 mt-1">Ventas sin Abonos</p>
          </div>
          <div class="text-center p-4 rounded-lg" 
               :class="reporte.analisis_calidad.cotizaciones_sin_productos === 0 ? 'bg-green-50' : 'bg-red-50'">
            <p class="text-2xl font-bold" :class="reporte.analisis_calidad.cotizaciones_sin_productos === 0 ? 'text-green-900' : 'text-red-900'">
              {{ reporte.analisis_calidad.cotizaciones_sin_productos }}
            </p>
            <p class="text-xs text-gray-600 mt-1">Sin Productos</p>
          </div>
        </div>
        <div class="mt-4 text-center">
          <span class="px-4 py-2 rounded-full text-sm font-bold"
                :class="getCalidadClass(reporte.analisis_calidad.calidad_general.calificacion)">
            Calificación: {{ reporte.analisis_calidad.calidad_general.calificacion }}
          </span>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
import reportes from "@/Services/reportes";

export default {
  name: "ReporteCotizacionesAvanzado",
  data() {
    return {
      reporte: null,
      cargando: false,
      error: null,
      filtros: {
        ano: new Date().getFullYear(),
        mes: new Date().getMonth() + 1,
        comparar_mes_anterior: true,
        incluir_detalles: true
      },
      meses: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ],
      years: []
    };
  },
  computed: {
    mostrarAlertasCriticas() {
      return this.reporte && 
             this.reporte.alertas && 
             this.reporte.alertas.criticas > 0 &&
             this.reporte.alertas.listado &&
             Array.isArray(this.reporte.alertas.listado);
    },
    alertasCriticas() {
      if (!this.mostrarAlertasCriticas) return [];
      return this.reporte.alertas.listado.filter(a => a && a.tipo === 'CRITICA');
    },
    alertasAdvertencias() {
      if (!this.reporte || !this.reporte.alertas || !this.reporte.alertas.listado) return [];
      return this.reporte.alertas.listado.filter(a => a && a.tipo === 'ADVERTENCIA');
    },
    alertasInfo() {
      if (!this.reporte || !this.reporte.alertas || !this.reporte.alertas.listado) return [];
      return this.reporte.alertas.listado.filter(a => a && a.tipo === 'INFO');
    },
    etapasEmbudo() {
      if (!this.reporte || !this.reporte.embudo_ventas || !this.reporte.embudo_ventas.etapas) return [];
      return Array.isArray(this.reporte.embudo_ventas.etapas) ? this.reporte.embudo_ventas.etapas : [];
    }
  },
  mounted() {
    this.generarYears();
    this.cargarReporte();
  },
  methods: {
    generarYears() {
      const currentYear = new Date().getFullYear();
      for (let i = currentYear; i >= currentYear - 5; i--) {
        this.years.push(i);
      }
    },
    
    async cargarReporte() {
      this.cargando = true;
      this.error = null;
      
      try {
        const response = await reportes.GET_REPORTE_COTIZACIONES_AVANZADO(this.filtros);
        
        // El servicio retorna directamente los datos (response.data)
        // Verificar si hay error en la respuesta
        if (response && response.error) {
          throw new Error(response.error);
        }
        
        // Verificar que tenga la estructura esperada
        if (response && response.metadata) {
          this.reporte = response;
          console.log('Reporte cargado:', this.reporte);
        } else {
          throw new Error('Respuesta del servidor no tiene el formato esperado');
        }
      } catch (err) {
        console.error('Error al cargar reporte:', err);
        this.error = err.message || 'Error al cargar el reporte';
      } finally {
        this.cargando = false;
      }
    },

    formatMonto(valor) {
      if (!valor) return '0.00';
      return Number(valor).toLocaleString('es-MX', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    },

    formatFecha(fecha) {
      if (!fecha) return '';
      const date = new Date(fecha);
      return date.toLocaleDateString('es-MX', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getConversionClass(tasa) {
      const valor = Number(tasa);
      if (valor >= 20) return 'bg-green-100 text-green-800';
      if (valor >= 10) return 'bg-yellow-100 text-yellow-800';
      return 'bg-red-100 text-red-800';
    },

    getClasificacionClass(clasificacion) {
      const clases = {
        'TOP_PERFORMER': 'bg-yellow-100 text-yellow-800 border border-yellow-400',
        'ALTO': 'bg-green-100 text-green-800',
        'MEDIO': 'bg-blue-100 text-blue-800',
        'BAJO': 'bg-orange-100 text-orange-800',
        'SIN_VENTAS': 'bg-red-100 text-red-800'
      };
      return clases[clasificacion] || 'bg-gray-100 text-gray-800';
    },

    getCalidadClass(calificacion) {
      const clases = {
        'EXCELENTE': 'bg-green-100 text-green-800',
        'BUENA': 'bg-blue-100 text-blue-800',
        'REGULAR': 'bg-yellow-100 text-yellow-800',
        'DEFICIENTE': 'bg-red-100 text-red-800'
      };
      return clases[calificacion] || 'bg-gray-100 text-gray-800';
    }
  }
};
</script>

<style scoped>
/* Animaciones suaves */
.bg-white {
  transition: all 0.3s ease;
}

.bg-white:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Scrollbar personalizado */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
